import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { createColumnHelper } from '@tanstack/react-table';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import CommonPagination from 'components/commonPagination/CommonPagination';
import CommonTable from 'components/commonTable/CommonTable';
import { ListMerchantData } from 'reactRedux/thunks/merchnatThunk/merchantThunk';

const MerchantAssignModal = ({
  isOpen,
  onClose,
  regionId,
  currentMerchants,
  onSubmit,
  isSubmitting,
}) => {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const dispatch = useDispatch();
  const { isLoading, merchantListData } = useSelector(
    (state) => state.MerchantSlice,
  );

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef(null);

  // Initialise selection from already-assigned merchants
  const initialIds = useMemo(
    () => currentMerchants.map((m) => m.merchantId),
    [currentMerchants],
  );
  const [selectedIds, setSelectedIds] = useState(initialIds);

  // Re-sync when modal opens with fresh currentMerchants
  useEffect(() => {
    if (isOpen) {
      setSelectedIds(currentMerchants.map((m) => m.merchantId));
      setPage(1);
      setSearch('');
      setDebouncedSearch('');
    }
  }, [isOpen, currentMerchants]);

  // Debounce search input
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  // Fetch merchant list whenever page / search / regionId changes
  const fetchMerchants = useCallback(() => {
    dispatch(
      ListMerchantData({
        page,
        limit: 10,
        regionId,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      }),
    );
  }, [dispatch, page, debouncedSearch, regionId]);

  useEffect(() => {
    if (isOpen) fetchMerchants();
  }, [isOpen, fetchMerchants]);

  const handleCheckboxChange = (merchantId) => {
    setSelectedIds((prev) =>
      prev.includes(merchantId)
        ? prev.filter((id) => id !== merchantId)
        : [...prev, merchantId],
    );
  };

  const handleSubmit = () => {
    onSubmit(selectedIds);
  };

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor((row) => row?.merchantId, {
      id: 'select',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Select
        </Text>
      ),
      cell: (info) => {
        const merchantId = info.getValue();
        return (
          <Checkbox
            isChecked={selectedIds.includes(merchantId)}
            onChange={() => handleCheckboxChange(merchantId)}
          />
        );
      },
    }),
    columnHelper.accessor((row) => row?.merchantId, {
      id: 'merchantId',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Merchant ID
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="600">
          {info.getValue() ?? 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row?.name, {
      id: 'name',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Name
        </Text>
      ),
      cell: (info) => (
        <Text
          color={textColor}
          fontSize="sm"
          fontWeight="600"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          maxW="200px"
        >
          {info.getValue() ?? 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row?.address, {
      id: 'address',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Address
        </Text>
      ),
      cell: (info) => (
        <Text
          color={textColor}
          fontSize="sm"
          fontWeight="600"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          maxW="250px"
        >
          {info.getValue() ?? 'N/A'}
        </Text>
      ),
    }),
  ];

  const list = merchantListData?.list || [];
  const total = merchantListData?.total || 0;
  const limit = merchantListData?.limit || 10;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text fontSize="lg" fontWeight="bold">
            Assign Merchants
          </Text>
          <Text fontSize="sm" color="gray.500" fontWeight="normal" mt={1}>
            {selectedIds.length} merchant{selectedIds.length !== 1 ? 's' : ''}{' '}
            selected
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {/* Search */}
          <Box mb={4}>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search merchants..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                bg="white"
                borderRadius="8px"
              />
            </InputGroup>
          </Box>

          <Box overflow="hidden">
            <CommonTable
              columns={columns}
              data={list}
              isLoading={isLoading}
              keyValue="merchantId"
              dynamicWidth={{
                select: '60px',
                merchantId: '120px',
                name: '220px',
                address: '270px',
              }}
            />
          </Box>

          <CommonPagination
            totalCount={total}
            activePage={page}
            pageCount={Math.ceil(total / limit) || 1}
            onPageChange={(p) => setPage(p)}
            itemsPerPage={limit}
          />
        </ModalBody>

        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Flex align="center" gap={2}>
            <Button
              bg="primary"
              color="white"
              _hover={{ bg: 'primary', opacity: 0.8 }}
              onClick={handleSubmit}
              isLoading={isSubmitting}
              loadingText="Assigning..."
              isDisabled={selectedIds.length === 0}
            >
              Apply to {selectedIds.length} Merchant
              {selectedIds.length !== 1 ? 's' : ''}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

MerchantAssignModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  regionId: PropTypes.number,
  currentMerchants: PropTypes.arrayOf(
    PropTypes.shape({
      merchantId: PropTypes.number,
    }),
  ),
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
};

MerchantAssignModal.defaultProps = {
  regionId: null,
  currentMerchants: [],
  isSubmitting: false,
};

export default MerchantAssignModal;
